import type { Order } from "../../../domain/entities/Order";
import type { OrderItem } from "../../../domain/entities/OrderItem";
import type { OrderId } from "../../../domain/value-objects/OrderId";
import type { BookingCode } from "../../../domain/value-objects/BookingCode";
import { createBookingCode } from "../../../domain/value-objects/BookingCode";
import { createMoney } from "../../../domain/value-objects/Money";
import { createOrderId } from "../../../domain/value-objects/OrderId";
import type { OrderQueryPort } from "../../../application/ports/OrderQueryPort";

interface OrdersHttpAdapterConfig {
  baseUrl: string;
  authorizationToken: string;
  currency: string;
}

interface OrderEventHttp {
  event: string;
  date: string;
}

interface OrderHttpResponse {
  code: string;
  client_email: string;
  amount: number;
  type: string;
  created_at: string;
  status: string;
  events_history: OrderEventHttp[];
}

interface BookingItemHttp {
  code: string;
  name: string;
  price: number;
}

interface BookingHttp {
  order_code: string;
  code?: string;
  items: BookingItemHttp[];
}

interface BookingListHttpResponse {
  items: BookingHttp[];
}

const ORDERS_BY_CODE_PATH = "/api/orders/by-code";
const BOOKINGS_PATH = "/api/orders/booking";

export class OrdersHttpAdapter implements OrderQueryPort {
  constructor(private readonly config: OrdersHttpAdapterConfig) {}

  async getById(orderId: OrderId): Promise<Order> {
    const orderResponse = await this.fetchOrder(orderId);
    const bookingDetails =
      orderResponse.type === "SERVICE" ? await this.fetchBookingDetails(orderId) : null;

    return {
      id: createOrderId(orderResponse.code),
      code: orderResponse.code,
      clientEmail: orderResponse.client_email,
      amount: createMoney(orderResponse.amount, this.config.currency),
      type: orderResponse.type === "SERVICE" ? "SERVICE" : "PRODUCTS",
      status: orderResponse.status,
      createdAt: new Date(orderResponse.created_at),
      eventsHistory: (orderResponse.events_history ?? []).map((event) => ({
        event: event.event,
        occurredAt: new Date(event.date),
      })),
      bookingCode: bookingDetails?.bookingCode,
      items: bookingDetails?.items ?? [],
    };
  }

  private async fetchOrder(orderId: OrderId): Promise<OrderHttpResponse> {
    const url = this.composeUrl(ORDERS_BY_CODE_PATH, { code: orderId });
    const response = await fetch(url, {
      headers: this.buildHeaders(),
    });

    if (!response.ok) {
      throw new Error("No fue posible obtener la información de la orden");
    }

    return response.json() as Promise<OrderHttpResponse>;
  }

  private async fetchBookingDetails(
    orderId: OrderId,
  ): Promise<{ bookingCode?: BookingCode; items: OrderItem[] } | null> {
    const url = this.composeUrl(BOOKINGS_PATH, { orderCode: orderId, limit: "1" });
    const response = await fetch(url, {
      headers: this.buildHeaders(),
    });

    if (!response.ok) {
      // When the booking is not found we return an empty list instead of failing the entire flow.
      if (response.status === 404) {
        return null;
      }

      throw new Error("No fue posible obtener los detalles de la reserva");
    }

    const payload = (await response.json()) as BookingListHttpResponse;
    const [booking] = payload.items ?? [];

    if (!booking) {
      return null;
    }

    const bookingItems = (booking.items ?? []).map((item) => ({
      code: item.code,
      name: item.name,
      price: createMoney(item.price, this.config.currency),
    }));

    const bookingCode = booking.code ? createBookingCode(booking.code) : undefined;

    return {
      bookingCode,
      items: bookingItems,
    };
  }

  private composeUrl(path: string, params?: Record<string, string>): string {
    const url = new URL(path, this.config.baseUrl);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    return url.toString();
  }

  private buildHeaders(): HeadersInit {
    return {
      Accept: "application/json",
      Authorization: `${this.config.authorizationToken}`,
    };
  }
}
